import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Box, Skeleton, styled } from '@mui/material'
import storeReCaptcha from 'GlobalStores/StoreReCaptcha'
import { useTranslate } from 'GlobalStores/Cores/Translator/useTranslate'
import { ReCAPTCHAV2_KEY, ReCAPTCHAV2QA_KEY } from 'Utils/Utils'
import { COLORS, RADII } from './OrderSummary/AddEditProduct/tokens'

const STATUS = {
    loading: 'loading',
    ready: 'ready',
    // grecaptcha never arrived, so there is no widget to retry. Only a fresh page load can fetch the
    // script again: react-async-script caches the failure and never re-appends the tag.
    scriptFailed: 'script-failed',
    // the widget itself reported an error (its own network call). grecaptcha is loaded, so remounting
    // the widget is a real retry.
    widgetFailed: 'widget-failed'
}

const ReCaptcha = observer(props => {
    const translate = useTranslate()
    const language = storeReCaptcha.currentLanguage

    const [verified, setVerified] = useState(false)
    const [status, setStatus] = useState(STATUS.loading)
    const [attempt, setAttempt] = useState(0)

    const RecaptchaKey = useMemo(() => {
        const liveDomain = ['ufg.expert']
        return liveDomain.includes(window.location.hostname) ? ReCAPTCHAV2_KEY : ReCAPTCHAV2QA_KEY
    }, [])

    useEffect(() => {
        props.handleReCaptCha(verified)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verified])

    // The widget cannot change language in place. react-google-recaptcha passes `hl` to grecaptcha only
    // inside explicitRender(), behind a `this._widgetId === undefined` guard, so the prop is read once per
    // widget and a later change does nothing. The `key` below remounts it instead, which drops any answer
    // the customer had already given - hence clearing `verified` here too.
    const renderedLanguage = useRef(language)

    useEffect(() => {
        if (renderedLanguage.current === language) {
            return
        }

        renderedLanguage.current = language
        setVerified(false)
        setStatus(STATUS.loading)
    }, [language])

    // Fires once the script settles - and also when it failed, so the entry has to be read rather than
    // assumed. Without this branch a blocked script leaves the skeleton up for good and Place order stays
    // locked with nothing on screen to solve.
    const handleScriptLoad = entry => {
        setStatus(entry && entry.errored ? STATUS.scriptFailed : STATUS.ready)
    }

    const handleWidgetError = () => {
        setVerified(false)
        setStatus(STATUS.widgetFailed)
    }

    // Google hands over a token when the answer is accepted and null when it expires; the caller only
    // needs to know whether the check holds right now.
    const handleChange = token => {
        setVerified(Boolean(token))
    }

    const retryWidget = () => {
        setVerified(false)
        setStatus(STATUS.loading)
        setAttempt(current => current + 1)
    }

    if (status === STATUS.scriptFailed || status === STATUS.widgetFailed) {
        return (
            <FailureBox role="alert">
                <Box component="div">
                    {translate('recaptcha_unavailable', { defaultDisplay: 'We could not load the security check. Please try again.' })}
                </Box>
                <RetryButton type="button" onClick={status === STATUS.scriptFailed ? () => window.location.reload() : retryWidget}>
                    {translate('retry', { defaultDisplay: 'Try again' })}
                </RetryButton>
            </FailureBox>
        )
    }

    return (
        <>
            <ReCAPTCHA
                key={`${language}-${attempt}`}
                onChange={handleChange}
                onExpired={() => setVerified(false)}
                onErrored={handleWidgetError}
                sitekey={RecaptchaKey}
                hl={language}
                asyncScriptOnLoad={handleScriptLoad}
                style={{ display: status === STATUS.ready ? 'flex' : 'none' }}
            />
            {status === STATUS.loading && (
                <Skeleton variant="rounded" width={'304px'} height={'78px'} sx={{ margin: '0', borderRadius: '12px' }} />
            )}
        </>
    )
})

// Box, not Typography: MainTemplate scales the body variants down under 430px and would win here
const FailureBox = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    width: 304px;
    padding: 14px;
    border-radius: ${RADII.box};
    background: ${COLORS.gray100};
    font-size: 13px;
    line-height: 18px;
    color: ${COLORS.error};
    text-align: center;
`

const RetryButton = styled('button')`
    border: none;
    cursor: pointer;
    padding: 8px 18px;
    border-radius: ${RADII.pill};
    background: ${COLORS.blue100};
    color: ${COLORS.blue600};
    font-family: inherit;
    font-size: 13px;
    line-height: 18px;
    font-weight: 600;

    &:hover {
        background: ${COLORS.blue200};
    }
`

export default ReCaptcha
